import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbModal, NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { ProtuneApiService } from '../../services/protune-api.service';
import { CarTrackSettings } from '../../models/car-track-settings';
import { SuspensionSettings } from '../../models/suspension-settings';
import { DrivetrainSettings } from '../../models/drivetrain-settings';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SavedSettings } from '../../models/saved-settings';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { PagedDataResult } from '../../models/paged-data-result';
import { AppSubscription, AuthService } from '../../services/auth.service';


@Component({
  selector: 'app-saved-settings-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgbPagination],
  templateUrl: './saved-settings-modal.component.html',
  styleUrl: './saved-settings-modal.component.scss'
})
export class SavedSettingsModalComponent implements OnInit, OnDestroy {
  @Input() mode: SavedSettingsMode;
  @Input() carTrackSettings: CarTrackSettings;
  @Input() suspensionSettings: SuspensionSettings;
  @Input() drivetrainSettings: DrivetrainSettings;

  @Output() savedSettingsId: number | null;
  @Output() name: string;
  @Output() selectedSavedSettings: SavedSettings;

  title: string;
  savedSettings: PagedDataResult<SavedSettings>
  pageSize: number = 15;
  page: number = 1;
  @Output() searchString: string = '';

  appSubscription: AppSubscription;

  private searchTerms$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private activeModal: NgbActiveModal, private modalService: NgbModal, private apiService: ProtuneApiService, private authService: AuthService) {

  }

  public get SavedSettingsMode() {
    return SavedSettingsMode;
  }

  ngOnInit(): void {
    this.authService.getSubscription()
      .subscribe(appSubscription => this.appSubscription = appSubscription);

    if (this.mode == SavedSettingsMode.load) {
      this.title = 'Load Settings';

      this.searchTerms$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.page = 1;
        this.search();
      });

      this.search();

    } else {
      this.title = 'Save Settings';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchTerms$.complete();
  }

  onSearchChange(value: string) {
    this.searchString = value;
    this.searchTerms$.next(value);
  }

  search() {
    this.apiService.getSavedSettings(this.page, this.pageSize, null, this.searchString)
      .subscribe({

        next: res => {
          this.savedSettings = res;
        }, error: err => console.log(err)
      })
  }

  selectSavedSettings(savedSettings: SavedSettings) {
    if (this.selectedSavedSettings?.id == savedSettings.id) {
      this.selectedSavedSettings = null;
    }
    else {
      this.selectedSavedSettings = savedSettings;
    }
  }

  save() {
    this.apiService.saveSettings(this.savedSettingsId, this.name, this.carTrackSettings, this.suspensionSettings, this.drivetrainSettings)
      .subscribe({
        next: (savedSettingsId: number) => {
          this.activeModal.close({
            savedSettingsId: savedSettingsId,
            name: this.name
          });
        }, error: err => console.log(err)
      });
  }

  clearSearch() {
    this.searchString = '';
    this.page = 1;
    this.search();
  }

  load() {
    this.activeModal.close(this.selectedSavedSettings);
  }

  delete(savedSetting: SavedSettings, $event: Event) {
    $event.stopPropagation();

    const modal = this.modalService.open(ConfirmationModalComponent);
    modal.componentInstance.title = 'Delete Saved Settings';
    modal.componentInstance.message = `Are you sure you want to delete ${savedSetting.name} settings?`;

    modal.closed.subscribe({
      next: (result: boolean) => {
        if (result == true) {
          this.apiService.deleteSavedSettings(savedSetting.id)
            .subscribe({
              next: () => {
                var idx = this.savedSettings.data.findIndex(ss => ss.id == savedSetting.id);
                this.savedSettings.data.splice(idx, 1);
              }
            })
        }
      }
    })
  }

  close() {
    this.activeModal.dismiss();
  }
}


export enum SavedSettingsMode {
  load = 0,
  save = 1
}
